// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Page permissions keyed off the navigation tree.
 * Format: `backoffice:<section>[:<sub-section>][:edit]`
 * `:edit` variants gate mutating UI (forms, action menus, save buttons).
 */
export const PAGE_PERMISSIONS = {
  approvals: "backoffice:approvals",
  approvalsEdit: "backoffice:approvals:edit",

  chains: "backoffice:chains",
  chainsEdit: "backoffice:chains:edit",
  chainsRegions: "backoffice:chains:regions",
  chainsRegionsEdit: "backoffice:chains:regions:edit",
  chainsSettings: "backoffice:chains:settings",
  chainsSettingsEdit: "backoffice:chains:settings:edit",
  chainsCutOffTimes: "backoffice:chains:cut-off-times",
  chainsCutOffTimesEdit: "backoffice:chains:cut-off-times:edit",
  chainsUptime: "backoffice:chains:uptime",
  chainsActivity: "backoffice:chains:activity",

  stores: "backoffice:stores",
  storesEdit: "backoffice:stores:edit",
  storesScorecard: "backoffice:stores:scorecard",
  storesHealth: "backoffice:stores:health",
  storesOperations: "backoffice:stores:operations",
  storesOperationsEdit: "backoffice:stores:operations:edit",
  storesHardware: "backoffice:stores:hardware",
  storesHardwareEdit: "backoffice:stores:hardware:edit",
  storesSchedule: "backoffice:stores:schedule",
  storesScheduleEdit: "backoffice:stores:schedule:edit",
  storesCutOffTimes: "backoffice:stores:cut-off-times",
  storesCutOffTimesEdit: "backoffice:stores:cut-off-times:edit",
  storesDevices: "backoffice:stores:devices",
  storesMap: "backoffice:stores:map",
  storesMapEdit: "backoffice:stores:map:edit",
  storesSettings: "backoffice:stores:settings",
  storesSettingsEdit: "backoffice:stores:settings:edit",
  storesActivity: "backoffice:stores:activity",

  settings: "backoffice:settings",
  settingsChains: "backoffice:settings:chains",
  settingsChainsEdit: "backoffice:settings:chains:edit",
  settingsStores: "backoffice:settings:stores",
  settingsStoresEdit: "backoffice:settings:stores:edit",
  settingsActivity: "backoffice:settings:activity",

  tasks: "backoffice:tasks",
  tasksEdit: "backoffice:tasks:edit",
  tasksAvailability: "backoffice:tasks:availability",
  tasksAvailabilityEdit: "backoffice:tasks:availability:edit",

  iamUsers: "backoffice:iam:users",
  iamUsersEdit: "backoffice:iam:users:edit",
  iamUsersAlerts: "backoffice:iam:users:alerts",
  iamUsersAlertsEdit: "backoffice:iam:users:alerts:edit",
  iamGroups: "backoffice:iam:groups",
  iamGroupsEdit: "backoffice:iam:groups:edit",
  iamVendors: "backoffice:iam:vendors",
  iamVendorsEdit: "backoffice:iam:vendors:edit",
  iamPermissions: "backoffice:iam:permissions",
  iamSso: "backoffice:iam:sso",
  ingressImports: "backoffice:ingress:imports",
  ingressImportsEdit: "backoffice:ingress:imports:edit",
  ingressTransformations: "backoffice:ingress:transformations",
  ingressTransformationsEdit: "backoffice:ingress:transformations:edit",
  ingressProductImages: "backoffice:ingress:product-images",
  ingressProductImagesEdit: "backoffice:ingress:product-images:edit",
  octoRules: "backoffice:octo:rules",
  octoRulesEdit: "backoffice:octo:rules:edit",
  octoVisualizer: "backoffice:octo:visualizer",
  hardwareFirmware: "backoffice:hardware:firmware",
  hardwareFirmwareEdit: "backoffice:hardware:firmware:edit",
  hardwareDevices: "backoffice:hardware:devices",
} as const;

export type PagePermission = (typeof PAGE_PERMISSIONS)[keyof typeof PAGE_PERMISSIONS];

/**
 * Does a granted permission string satisfy the required one?
 *
 * Wildcards:
 * - `prefix:*` matches `prefix` itself and any `prefix:...` that does NOT end in `:edit`.
 * - `prefix:*:edit` matches any `prefix:...:edit`.
 * Otherwise requires an exact match.
 */
export function permissionMatches(granted: string, required: string): boolean {
  if (granted === required) return true;
  if (granted.endsWith(":*:edit")) {
    const prefix = granted.slice(0, -":*:edit".length);
    return required.startsWith(`${prefix}:`) && required.endsWith(":edit");
  }
  if (granted.endsWith(":*")) {
    const prefix = granted.slice(0, -2);
    if (required.endsWith(":edit")) return false;
    return required === prefix || required.startsWith(`${prefix}:`);
  }
  return false;
}

/** User-level permission check. Returns true if any granted permission satisfies the required one. */
export function userHasPermission(user: { permissions: readonly string[] } | null, required: string): boolean {
  if (!user) return false;
  return user.permissions.some((p) => permissionMatches(p, required));
}
